import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, Alert, Button, Select, DatePicker, Statistic, Table, Tag, Timeline } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
         BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { MessageOutlined, UserOutlined, RobotOutlined, ClockCircleOutlined } from '@ant-design/icons';
import './ChatAnalyticsPage.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * 聊天数据分析和可视化页面
 */
const ChatAnalyticsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [chatData, setChatData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('week');
  const [analysisType, setAnalysisType] = useState('overview');

  // 模拟聊天数据 (实际项目中应该从API获取)
  const mockChatData = [
    {
      id: 1,
      type: 'user',
      content: '我今天感觉很累',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      session: 'session_1',
      sentiment: 'negative',
      topics: ['疲劳', '情绪低落']
    },
    {
      id: 2,
      type: 'ai',
      content: '我理解你的疲惫感。可以告诉我具体是什么让你感到累吗？',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 60000),
      session: 'session_1',
      sentiment: 'neutral',
      topics: ['关怀', '询问']
    },
    {
      id: 3,
      type: 'user',
      content: '工作压力太大了，项目deadline很紧',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 120000),
      session: 'session_1',
      sentiment: 'negative',
      topics: ['工作压力', '时间管理']
    },
    {
      id: 4,
      type: 'user',
      content: '今天心情不错，完成了一个重要任务',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      session: 'session_2',
      sentiment: 'positive',
      topics: ['成就感', '积极情绪']
    },
    {
      id: 5,
      type: 'ai',
      content: '太棒了！完成重要任务一定很有成就感。可以分享一下是什么任务吗？',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000 + 60000),
      session: 'session_2',
      sentiment: 'positive',
      topics: ['祝贺', '询问细节']
    }
  ];

  // 图表颜色配置
  const COLORS = {
    positive: '#52c41a',
    negative: '#ff4d4f',
    neutral: '#faad14',
    user: '#1890ff',
    ai: '#722ed1'
  };

  // 分析类型选项
  const analysisTypeOptions = [
    { value: 'overview', label: '总览分析' },
    { value: 'sentiment', label: '情感分析' },
    { value: 'topics', label: '话题分析' },
    { value: 'activity', label: '活跃度分析' },
    { value: 'sessions', label: '会话分析' }
  ];

  // 时间范围选项
  const timeRangeOptions = [
    { value: 'today', label: '今天' },
    { value: 'week', label: '本周' },
    { value: 'month', label: '本月' },
    { value: 'quarter', label: '本季度' }
  ];

  // 加载聊天数据
  const loadChatData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 模拟API调用延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      setChatData(mockChatData);
    } catch (err) {
      console.error('加载聊天数据失败:', err);
      setError('加载数据失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 处理聊天数据用于不同分析
  const processDataForAnalysis = (data, type) => {
    if (!data || data.length === 0) return [];

    switch (type) {
      case 'overview':
        return processOverviewData(data);
      case 'sentiment':
        return processSentimentData(data);
      case 'topics':
        return processTopicsData(data);
      case 'activity':
        return processActivityData(data);
      case 'sessions':
        return processSessionsData(data);
      default:
        return data;
    }
  };

  // 处理总览数据
  const processOverviewData = (data) => {
    const dailyStats = {};
    
    data.forEach(message => {
      const date = new Date(message.timestamp).toLocaleDateString();
      if (!dailyStats[date]) {
        dailyStats[date] = { 
          date, 
          userMessages: 0, 
          aiMessages: 0, 
          totalMessages: 0,
          sessions: new Set()
        };
      }
      
      dailyStats[date].totalMessages++;
      if (message.type === 'user') {
        dailyStats[date].userMessages++;
      } else {
        dailyStats[date].aiMessages++;
      }
      dailyStats[date].sessions.add(message.session);
    });

    return Object.values(dailyStats).map(stat => ({
      ...stat,
      sessionCount: stat.sessions.size
    })).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // 处理情感数据
  const processSentimentData = (data) => {
    const sentimentCounts = { positive: 0, negative: 0, neutral: 0 };
    
    data.forEach(message => {
      if (sentimentCounts.hasOwnProperty(message.sentiment)) {
        sentimentCounts[message.sentiment]++;
      }
    });

    return Object.entries(sentimentCounts).map(([sentiment, count]) => ({
      sentiment,
      count,
      percentage: ((count / data.length) * 100).toFixed(1)
    }));
  };

  // 处理话题数据
  const processTopicsData = (data) => {
    const topicCounts = {};
    
    data.forEach(message => {
      if (message.topics) {
        message.topics.forEach(topic => {
          topicCounts[topic] = (topicCounts[topic] || 0) + 1;
        });
      }
    });

    return Object.entries(topicCounts)
      .map(([topic, count]) => ({ topic, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  // 处理活跃度数据
  const processActivityData = (data) => {
    const hourlyActivity = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      count: 0
    }));

    data.forEach(message => {
      const hour = new Date(message.timestamp).getHours();
      hourlyActivity[hour].count++;
    });

    return hourlyActivity;
  };

  // 处理会话数据
  const processSessionsData = (data) => {
    const sessions = {};
    
    data.forEach(message => {
      if (!sessions[message.session]) {
        sessions[message.session] = {
          session: message.session,
          messageCount: 0,
          startTime: message.timestamp,
          endTime: message.timestamp,
          userMessages: 0,
          aiMessages: 0,
          sentiments: { positive: 0, negative: 0, neutral: 0 }
        };
      }
      
      const sessionData = sessions[message.session];
      sessionData.messageCount++;
      
      if (message.timestamp < sessionData.startTime) {
        sessionData.startTime = message.timestamp;
      }
      if (message.timestamp > sessionData.endTime) {
        sessionData.endTime = message.timestamp;
      }
      
      if (message.type === 'user') {
        sessionData.userMessages++;
      } else {
        sessionData.aiMessages++;
      }
      
      if (sessionData.sentiments.hasOwnProperty(message.sentiment)) {
        sessionData.sentiments[message.sentiment]++;
      }
    });

    return Object.values(sessions).map(session => ({
      ...session,
      duration: Math.round((session.endTime - session.startTime) / 60000), // 分钟
      dominantSentiment: Object.entries(session.sentiments)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || 'neutral'
    }));
  };

  // 渲染统计卡片
  const renderStatCards = () => {
    if (!chatData || chatData.length === 0) return null;

    const userMessages = chatData.filter(m => m.type === 'user').length;
    const aiMessages = chatData.filter(m => m.type === 'ai').length;
    const uniqueSessions = new Set(chatData.map(m => m.session)).size;
    const avgMessagesPerSession = (chatData.length / uniqueSessions).toFixed(1);

    return (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="总消息数" 
              value={chatData.length}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="用户消息" 
              value={userMessages}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="AI回复" 
              value={aiMessages}
              prefix={<RobotOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="会话数量" 
              value={uniqueSessions}
              suffix="个"
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染分析图表
  const renderAnalysisChart = () => {
    const data = processDataForAnalysis(chatData, analysisType);
    
    if (!data || data.length === 0) {
      return <div style={{ textAlign: 'center', padding: 40 }}>暂无数据</div>;
    }

    switch (analysisType) {
      case 'overview':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="userMessages" stroke="#1890ff" name="用户消息" />
              <Line type="monotone" dataKey="aiMessages" stroke="#722ed1" name="AI回复" />
              <Line type="monotone" dataKey="sessionCount" stroke="#52c41a" name="会话数" />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'sentiment':
        return (
          <Row gutter={16}>
            <Col span={12}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ sentiment, percentage }) => `${sentiment} ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.sentiment] || '#8884d8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Col>
            <Col span={12}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sentiment" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1890ff" />
                </BarChart>
              </ResponsiveContainer>
            </Col>
          </Row>
        );

      case 'topics':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="topic" type="category" width={100} />
              <Tooltip />
              <Bar dataKey="count" fill="#faad14" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'activity':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Area 
                type="monotone" 
                dataKey="count" 
                stroke="#13c2c2" 
                fill="rgba(19, 194, 194, 0.3)"
                name="消息数量"
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'sessions':
        const columns = [
          {
            title: '会话ID',
            dataIndex: 'session',
            key: 'session'
          },
          {
            title: '消息数',
            dataIndex: 'messageCount',
            key: 'messageCount',
            sorter: (a, b) => a.messageCount - b.messageCount
          },
          {
            title: '用户消息',
            dataIndex: 'userMessages',
            key: 'userMessages'
          },
          {
            title: 'AI回复',
            dataIndex: 'aiMessages',
            key: 'aiMessages'
          },
          {
            title: '持续时间',
            dataIndex: 'duration',
            key: 'duration',
            render: (duration) => `${duration} 分钟`
          },
          {
            title: '主要情感',
            dataIndex: 'dominantSentiment',
            key: 'dominantSentiment',
            render: (sentiment) => (
              <Tag color={COLORS[sentiment]}>{sentiment}</Tag>
            )
          }
        ];

        return <Table dataSource={data} columns={columns} rowKey="session" />;

      default:
        return <div>不支持的分析类型</div>;
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    loadChatData();
  }, [selectedTimeRange]);

  return (
    <div className="chat-analytics-page">
      <div className="page-header">
        <h1>💬 聊天数据分析</h1>
        <p>分析你的聊天模式、情感倾向和话题偏好</p>
      </div>

      {/* 控制面板 */}
      <Card className="control-panel" style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <label>时间范围：</label>
            <Select
              value={selectedTimeRange}
              onChange={setSelectedTimeRange}
              style={{ width: '100%' }}
            >
              {timeRangeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <label>分析类型：</label>
            <Select
              value={analysisType}
              onChange={setAnalysisType}
              style={{ width: '100%' }}
            >
              {analysisTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Button type="primary" onClick={loadChatData} loading={isLoading}>
              刷新数据
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 错误提示 */}
      {error && (
        <Alert
          message="数据加载失败"
          description={error}
          type="error"
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 加载状态 */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>正在加载数据...</p>
        </div>
      )}

      {/* 统计概览 */}
      {!isLoading && !error && renderStatCards()}

      {/* 分析图表 */}
      {!isLoading && !error && (
        <Card title={`${analysisTypeOptions.find(opt => opt.value === analysisType)?.label}`}>
          {renderAnalysisChart()}
        </Card>
      )}
    </div>
  );
};

export default ChatAnalyticsPage;